/**
 * API endpoint configuration.
 *
 * Endpoints are read from environment variables at build time so that no URL
 * is ever hardcoded in a component and nothing sensitive lives in source.
 *
 * Production path:
 *   React frontend -> HTTPS -> AWS Lambda Function URL -> AWS Lambda
 *
 * NOTE: These values are PUBLIC by definition -- anything prefixed with
 * REACT_APP_ is embedded in the client bundle. Never put secrets, API keys,
 * credentials, or AWS secrets here. Server-side secrets belong in AWS Secrets
 * Manager behind the Lambda.
 */

/**
 * Absolute URL of the contact-relay Lambda Function URL, e.g.
 * https://xxxxxxxxxxxxxxxx.lambda-url.us-east-1.on.aws/
 *
 * Empty string when unset, which leaves the consultation form inert rather
 * than posting into the void. The relay is a Function URL rather than a
 * path-routed API, so the URL is called as-is; the trailing slash is stripped
 * so a value copied from the AWS console cannot produce a double slash.
 */
export const CONTACT_API_URL = (process.env.REACT_APP_CONTACT_API_URL || '').replace(/\/+$/, '');

/**
 * Absolute assistant endpoint -- the shared Lambda Function URL, supplied by
 * REACT_APP_CHAT_API_URL so the name matches the sibling qaaslab-ai-site.
 * Empty string when unset, which disables the assistant surface.
 */
export const AI_ASSISTANT_URL = (process.env.REACT_APP_CHAT_API_URL || '').replace(/\/+$/, '');

/**
 * Tenant identifier sent with every assistant request.
 *
 * Supplied by REACT_APP_TENANT_ID in .env.production. There is deliberately no
 * literal fallback: a hardcoded second copy would drift from the environment
 * file, and a build that lost the variable would silently post the wrong tenant
 * instead of failing visibly.
 *
 * This value is PUBLIC: it is embedded in the client bundle and can be read or
 * forged by anyone. It selects configuration, it does not authorise anything --
 * the Lambda must validate it rather than trust it.
 */
export const TENANT_ID = process.env.REACT_APP_TENANT_ID || '';

/** Request timeout in milliseconds. */
export const REQUEST_TIMEOUT_MS = Number(
  process.env.REACT_APP_API_TIMEOUT_MS || 15000
);

/** True when the consultation form can reach the contact-relay Lambda. */
export const isContactRelayConfigured = () => Boolean(CONTACT_API_URL);

/**
 * True when the assistant can make a valid request: the Lambda Function URL
 * and the tenant ID must both be configured. A missing tenant ID disables the
 * chat rather than posting a request the shared Lambda cannot route.
 */
export const isAiApiConfigured = () => Boolean(AI_ASSISTANT_URL && TENANT_ID);

/**
 * Resolves the URL for a contact submission, or null when the relay is not
 * configured. A Function URL is not path-routed, so nothing is appended.
 */
export const resolveContactUrl = () => (CONTACT_API_URL ? CONTACT_API_URL : null);
